'use client';
import styled, { css } from 'styled-components';
import { generateContentIconStyle } from '@ui/icons';

const isColumnWithDescription = props =>
  props.orientation === 'column' && props.showDescription;

export const CardWrapper = styled.div`
  position: relative;
  display: inline-block;
  margin-top: ${props => props.$offsetBadge || 0}px;
`;

export const Card = styled.div`
  border-radius: ${({ $borderRadius }) =>
    $borderRadius ? `${$borderRadius}px` : '24px'};
  border: 1px solid
    ${props =>
      props.checked
        ? props.theme.colors.text.link
        : props.theme.colors.border.light};
  display: inline-flex;
  flex-direction: column;
  user-select: none;
  overflow: hidden; /* prevent overflow on rounded corners */
  position: relative; /* allow absolute positioning of child elements */
  width: ${({ $mobileWidth, width }) =>
    $mobileWidth ? `${$mobileWidth}px` : `${width}px`};
  max-height: ${props =>
    props.orientation === 'column'
      ? props.showDescription && !props.$alignColumnContent === 'center'
        ? '160px'
        : '178px'
      : props.showSelectionButton
      ? '190px'
      : '110px'};
  background: ${({ theme, unavailable, $notSelectable, $bgColor, checked }) =>
    unavailable || $notSelectable
      ? theme.colors.background.lightest
      : checked
      ? theme.colors.background.checked
      : theme.colors.background[$bgColor]};
  opacity: ${({ $notSelectable }) => ($notSelectable ? 0.5 : 1)};
  color: ${({ theme }) => theme.colors.text.primary};
  padding: ${props =>
    props.orientation === 'column' && props.$offsetBadge
      ? props.theme.spacing(5, 0, 2)
      : props.orientation === 'column'
      ? props.theme.spacing(2)
      : props.$offsetBadge
      ? props.theme.spacing(4, 0)
      : '0'};

  @media ${props => props.theme.laptop} {
    width: ${props => `${props.width}px`};
  }
`;

export const CardContent = styled.div`
  display: flex;
  align-items: ${({ $alignColumnContent }) =>
    $alignColumnContent === 'left' ? 'flex-start' : 'center'};
  flex-direction: ${props => props.orientation};
  flex: 1;
  gap: 1px;
  padding: ${props =>
    props.orientation === 'column' && props.$alignColumnContent === 'center'
      ? props.theme.spacing(2, 0)
      : props.orientation === 'column' && !props.showDescription
      ? '0'
      : props.showSelectionButton
      ? props.theme.spacing(3, 5)
      : props.theme.spacing(2)};

  @media ${props => props.theme.laptop} {
    padding: ${props =>
      isColumnWithDescription(props)
        ? props.theme.spacing(2)
        : props.theme.spacing(1, 2)};
  }
`;

export const StyledImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $alignColumnContent }) =>
    $alignColumnContent === 'center' ? 'center' : 'left'};
  overflow: hidden;
  object-position: left;
  object-fit: cover;
  height: ${props =>
    props.$imgHeight
      ? `${props.$imgHeight}px`
      : props.orientation === 'column' && props.showDescription
      ? '50px'
      : '85px'};
  width: ${props =>
    props.$imgWidth && props.orientation === 'column'
      ? `${props.$imgWidth}px`
      : props.$imgWidth
      ? `${props.$imgWidth + 12}px`
      : props.orientation === 'column'
      ? props.showDescription
        ? '65px'
        : `85px`
      : '100px'};
  filter: ${props => props.unavailable && 'grayscale(100%) brightness(93%)'};
  margin: ${props =>
    isColumnWithDescription(props)
      ? props.theme.spacing(0, 0, 2, 0)
      : props.showDescription
      ? props.theme.spacing(0, 0, 0, 2)
      : props.theme.spacing(0, 2)};
  ${props =>
    !props.checked &&
    !props.unavailable &&
    !props.showDescription &&
    css`
      &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    `};
  img {
    max-height: 100%;
    max-width: ${props =>
      !!props.$imgWidth
        ? `${props.$imgWidth}px`
        : isColumnWithDescription(props)
        ? '65px'
        : '82px'};
  }
`;

export const Info = styled.div`
  max-width: 200px;
  text-align: left;
  margin-top: ${props =>
    props.theme.spacing(props.$orientation === 'row' ? 2 : 0)};
`;

export const CardHeader = styled.div`
  color: ${props => props.theme.colors.text.primary};
  display: flex;
  justify-content: ${({ $alignColumnContent }) =>
    $alignColumnContent === 'left' ? 'flex-start' : 'center'};
  gap: ${props => props.theme.spacing(4)};
`;

export const Name = styled.h5`
  ${props =>
    props.$typography
      ? props.theme.typography[props.$typography]
      : props.theme.typography.heading4}
  text-transform: capitalize;
`;

export const Description = styled.p`
  ${({ theme }) => theme.typography.subheading}
  color: ${props => props.theme.colors.text.secondary};
`;

export const MoreDetails = styled.button`
  ${({ theme }) => theme.typography.body}
  left: ${props => props.theme.spacing(4)};
  bottom: ${props => props.theme.spacing(8)};
  display: flex;
  border: none;
  visibility: ${props =>
    props.unavailable || props.disabled ? 'hidden' : 'visible'};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.text.link};
  text-align: left;
  z-index: 1;
  padding: ${props =>
    props.$orientation === 'row' ? props.theme.spacing(0, 0, 2) : '0'};

  svg {
    margin-left: ${props => props.theme.spacing(1)};
  }
  &:focus {
    border-radius: ${props => props.theme.spacing(2)};
    outline: 2px solid ${({ theme }) => theme.colors.neutral.darkest};
  }
`;

export const CheckboxContainer = styled.div`
  display: flex;
  justify-content: center;
  border-radius: 200px;
  padding: ${props => props.theme.spacing(4)};
  background: ${props =>
    props.checked
      ? props.theme.colors.secondary.yellow
      : props.theme.colors.secondary.plum};
  background: ${props => props.unavailable && 'transparent'};
  min-width: 126px;
  max-width: 300px;
  height: 48px;
  align-items: center;
  ${props =>
    (props.icon || props.checked || props.unavailable) &&
    css`
      gap: ${props.theme.spacing(3)};
    `};

  ${props =>
    props.unavailable &&
    css`
      position: absolute;
      left: 0;
      right: 0;
      margin-left: auto;
      margin-right: auto;
      justify-content: space-between;
    `}
`;

export const ButtonWrapper = styled.div`
  position: static;
  width: 100%;
  padding: 2px;
`;

export const Label = styled.label`
  display: flex;
  align-items: center;
  &:hover {
    cursor: pointer;
  }

  &::before {
    ${props =>
      props.checked
        ? generateContentIconStyle('check')
        : props.unavailable
        ? generateContentIconStyle('close')
        : props.icon
        ? generateContentIconStyle(props.icon)
        : ''};
    width: ${props =>
      (props.icon || props.checked || props.unavailable) && '16px'};
    height: ${props =>
      (props.icon || props.checked || props.unavailable) && '16px'};
    background: ${props =>
      props.checked || props.unavailable
        ? props.theme.colors.text.primary
        : props.theme.colors.text.lightest};
  }

  &::after {
    content: '';
    border-radius: 32px;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: none;
  }
`;
export const LabelText = styled.span`
  ${({ theme }) => theme.typography.button}
  text-align: center;
  color: ${({ theme, checked, unavailable }) =>
    checked || unavailable
      ? theme.colors.text.primary
      : theme.colors.text.lightest};
`;

export const BadgeWrapper = styled.div`
  border-radius: 20px;
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
  p {
    ${({ theme }) => theme.fontFamily.condensed}
    padding: ${props => props.theme.spacing(4)};
    max-height: 20px;
  }
`;

export const IconContainer = styled.div`
  position: absolute;
  top: ${({ $orientation, $offsetBadge }) =>
    $orientation === 'column' && $offsetBadge ? '28px' : '12px'};
  right: ${({ $orientation, $offsetBadge }) =>
    $orientation === 'column' && $offsetBadge ? '24px' : '12px'};
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  min-height: 28px;
`;
